import { Component } from '@theme/component';

/**
 * Tabs component
 *
 * Builds its own tablist from the panels it contains. A theme block's settings
 * are not readable from the section that hosts it, so each panel carries its
 * label on a data attribute and the buttons are generated here. That also means
 * a Tabs block nested inside a tab needs no extra wiring - refs resolve to the
 * closest component, so the inner and outer tablists never see each other.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} list
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class Tabs extends Component {
  requiredRefs = ['list'];

  /** @type {HTMLButtonElement[]} */
  #buttons = [];

  connectedCallback() {
    super.connectedCallback();

    this.#build();

    this.refs.list.addEventListener('click', this.#handleClick);
    this.refs.list.addEventListener('keydown', this.#handleKeydown);

    // The theme editor reveals a nested block by scrolling to it, which fails
    // silently when its panel is hidden. Open the owning tab instead.
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    // Hand panel visibility over from the pre-hydration CSS rule to [hidden].
    this.classList.add('tabs--ready');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.list.removeEventListener('click', this.#handleClick);
    this.refs.list.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return this.refs.panels || [];
  }

  get selectedIndex() {
    const index = this.#buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    return index === -1 ? 0 : index;
  }

  /**
   * Activate a tab and reveal its panel.
   *
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  select(index, { focus = false } = {}) {
    if (index < 0 || index >= this.#buttons.length) return;

    this.#buttons.forEach((button, i) => {
      const isSelected = i === index;
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      button.tabIndex = isSelected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== index);
    });

    if (focus) this.#buttons[index]?.focus();
  }

  #build() {
    const list = this.refs.list;
    list.textContent = '';

    this.#buttons = this.panels.map((panel, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tabs__tab';
      button.setAttribute('role', 'tab');
      button.id = panel.getAttribute('aria-labelledby') || `${panel.id}-tab`;
      button.setAttribute('aria-controls', panel.id);

      const label = panel.dataset.tabLabel?.trim();
      const sublabel = panel.dataset.tabSublabel?.trim();

      if (label || !sublabel) {
        button.append(this.#line('tabs__tab-label', label || `Tab ${index + 1}`));
      }

      if (sublabel) {
        button.append(this.#line('tabs__tab-sublabel', sublabel));
      }

      list.append(button);
      return button;
    });

    this.select(0);
  }

  /**
   * @param {string} className
   * @param {string} text
   */
  #line(className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (!tab) return;

    const index = this.#buttons.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index !== -1) this.select(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const count = this.#buttons.length;
    if (!count) return;

    const current = this.selectedIndex;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % count;
        break;
      case 'ArrowLeft':
        next = (current - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Only react to panels this component owns; a nested Tabs block handles its
    // own, and the event keeps bubbling so the outer tab opens too.
    const panel = target.closest('[role="tabpanel"]');
    if (!panel) return;

    const index = this.panels.indexOf(/** @type {HTMLElement} */ (panel));
    if (index !== -1) this.select(index);
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', Tabs);
}
